import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../../api";
import { Spinner } from "../../components/Logo";
import { Modal, Field, ConfirmButton } from "../../components/Modal";
import { StatusChip } from "../../components/ui";
import { RichTextEditor } from "../../components/RichTextEditor";
import { ImageUpload } from "../../components/Uploads";
import { ArrowLeft, Plus, Pencil, Trash2, Zap, GripVertical, Trash } from "lucide-react";

const emptyModule = { title: "", content: "", image_url: "", examples: "", key_takeaways: "", xp_reward: 50, status: "published" };

export default function AdminPathEditor() {
  const { pathId } = useParams();
  const [modules, setModules] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyModule);
  const [editId, setEditId] = useState(null);
  const [quizFor, setQuizFor] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/learning/paths/${pathId}/modules`).then((r) => setModules(r.data.modules)).catch(() => {});
  }, [pathId]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setForm(emptyModule); setEditId(null); setModalOpen(true); };
  const openEdit = (m) => {
    setForm({
      title: m.title || "",
      content: m.content || "",
      image_url: m.image_url || "",
      examples: m.examples || "",
      key_takeaways: m.key_takeaways || "",
      xp_reward: m.xp_reward ?? 50,
      status: m.status || "published",
    });
    setEditId(m.id);
    setModalOpen(true);
  };

  const save = async () => {
    const payload = {
      title: form.title,
      content: form.content || "",
      image_url: form.image_url || "",
      examples: form.examples || "",
      key_takeaways: form.key_takeaways || "",
      xp_reward: Number(form.xp_reward) || 0,
      status: form.status,
    };
    try {
      if (editId) await api.patch(`/admin/modules/${editId}`, payload);
      else await api.post("/admin/modules", { ...payload, path_id: pathId });
      toast.success(editId ? "Module updated" : "Module created");
      setModalOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/modules/${id}`); toast.success("Module deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-path-editor">
      <Link to="/admin/paths" className="btn-ghost text-sm mb-4 -ml-2"><ArrowLeft size={16} /> Back to paths</Link>
      <div className="flex items-center justify-between mb-6">
        <div><div className="overline mb-1">Modules & assessments</div><h1 className="font-heading text-2xl font-bold">Path builder</h1></div>
        <button className="btn-primary" onClick={openNew} data-testid="new-module-btn"><Plus size={18} /> Add module</button>
      </div>

      {!modules ? <Spinner /> : modules.length === 0 ? (
        <div className="card p-10 text-center text-slate-500">No modules yet. Add your first module.</div>
      ) : (
        <div className="space-y-3">
          {modules.map((m, i) => (
            <div key={m.id} className="card p-4 flex items-center gap-3" data-testid={`admin-module-${m.id}`}>
              <GripVertical size={18} className="text-slate-600" />
              <span className="font-mono text-slate-500 text-sm">#{i + 1}</span>
              <div className="flex-1">
                <div className="font-medium flex items-center gap-2">{m.title} {m.has_quiz && <span className="chip bg-[#0ea5e9]/15 text-[#38bdf8]"><Zap size={12} /> Quiz</span>}</div>
              </div>
              <StatusChip status={m.status} />
              <span className="chip bg-[#F26522]/10 text-[#F26522]">+{m.xp_reward} XP</span>
              <div className="flex items-center gap-1">
                <button className="btn-ghost !px-2 text-[#38bdf8]" title="Quiz builder" onClick={() => setQuizFor(m)} data-testid={`quiz-builder-${m.id}`}><Zap size={16} /></button>
                <button className="btn-ghost !px-2" title="Edit" onClick={() => openEdit(m)}><Pencil size={16} /></button>
                <ConfirmButton className="btn-ghost !px-2 text-red-400" onConfirm={() => del(m.id)}><Trash2 size={16} /></ConfirmButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editId ? "Edit module" : "New module"} size="xl">
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <Field label="Title"><input className="input" value={form.title} onChange={set("title")} data-testid="module-title" /></Field>
          <Field label="Theory content"><RichTextEditor value={form.content} onChange={(html) => setForm({ ...form, content: html })} placeholder="Write the lesson. Use the toolbar for headings, lists, code blocks and images..." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Header image (Image dimensions should be 1040 x 208 px)"><ImageUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} height="h-28" /></Field>
            <Field label="XP reward"><input className="input" type="number" value={form.xp_reward} onChange={set("xp_reward")} /></Field>
          </div>
          <Field label="Practical"><RichTextEditor value={form.examples} onChange={(html) => setForm({ ...form, examples: html })} placeholder="Walk through a practical example. Use the toolbar for headings, lists, code blocks and images..." /></Field>
          <Field label="Key takeaways"><textarea className="input min-h-16" value={form.key_takeaways || ""} onChange={set("key_takeaways")} /></Field>
          <Field label="Status">
            <select className="input" value={form.status} onChange={set("status")}>{["published", "draft", "archived"].map((s) => <option key={s}>{s}</option>)}</select>
          </Field>
          <button className="btn-primary w-full" onClick={save} data-testid="save-module-btn">{editId ? "Update module" : "Create module"}</button>
        </div>
      </Modal>

      {quizFor && <QuizBuilder module={quizFor} onClose={() => { setQuizFor(null); load(); }} />}
    </div>
  );
}

const emptyQuestion = () => ({ text: "", type: "single", difficulty: "Medium", explanation: "", answers: [{ text: "", is_correct: true }, { text: "", is_correct: false }] });

function QuizBuilder({ module, onClose }) {
  const [quiz, setQuiz] = useState({ title: `${module.title} Quiz`, pass_threshold: 70, xp_reward: 100, questions: [] });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`/admin/modules/${module.id}/quiz`).then((r) => {
      if (r.data.quiz) {
        const q = r.data.quiz;
        setQuiz({ title: q.title, pass_threshold: q.pass_threshold, xp_reward: q.xp_reward, questions: q.questions.map((qq) => ({ text: qq.text, type: qq.type, difficulty: qq.difficulty, explanation: qq.explanation || "", answers: qq.answers.map((a) => ({ text: a.text, is_correct: a.is_correct })) })) });
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [module.id]);

  const upd = (patch) => setQuiz({ ...quiz, ...patch });
  const updQ = (i, patch) => { const qs = [...quiz.questions]; qs[i] = { ...qs[i], ...patch }; upd({ questions: qs }); };
  const updA = (qi, ai, patch) => { const qs = [...quiz.questions]; const ans = [...qs[qi].answers]; ans[ai] = { ...ans[ai], ...patch }; qs[qi] = { ...qs[qi], answers: ans }; upd({ questions: qs }); };
  const setCorrect = (qi, ai) => {
    const qs = [...quiz.questions];
    if (qs[qi].type === "multi") { const ans = [...qs[qi].answers]; ans[ai] = { ...ans[ai], is_correct: !ans[ai].is_correct }; qs[qi].answers = ans; }
    else qs[qi].answers = qs[qi].answers.map((a, idx) => ({ ...a, is_correct: idx === ai }));
    upd({ questions: qs });
  };

  const save = async () => {
    for (const q of quiz.questions) {
      if (!q.text.trim() || q.answers.filter((a) => a.text.trim()).length < 2 || !q.answers.some((a) => a.is_correct)) {
        toast.error("Each question needs text, 2+ options and a correct answer"); return;
      }
    }
    try { await api.post(`/admin/modules/${module.id}/quiz`, quiz); toast.success("Quiz saved"); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onClose={onClose} title={`Quiz builder — ${module.title}`} size="xl">
      {!loaded ? <Spinner /> : (
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="quiz-builder">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Quiz title"><input className="input" value={quiz.title} onChange={(e) => upd({ title: e.target.value })} /></Field>
            <Field label="Pass %"><input className="input" type="number" value={quiz.pass_threshold} onChange={(e) => upd({ pass_threshold: +e.target.value })} /></Field>
            <Field label="XP reward"><input className="input" type="number" value={quiz.xp_reward} onChange={(e) => upd({ xp_reward: +e.target.value })} /></Field>
          </div>

          {quiz.questions.map((q, qi) => (
            <div key={qi} className="card p-4 space-y-3 border-white/10" data-testid={`qb-question-${qi}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="overline">Question {qi + 1}</span>
                <button className="btn-ghost !px-2 text-red-400" onClick={() => upd({ questions: quiz.questions.filter((_, i) => i !== qi) })}><Trash size={15} /></button>
              </div>
              <input className="input" placeholder="Question text" value={q.text} onChange={(e) => updQ(qi, { text: e.target.value })} data-testid={`qb-qtext-${qi}`} />
              <div className="grid grid-cols-2 gap-3">
                <select className="input" value={q.type} onChange={(e) => updQ(qi, { type: e.target.value })}>
                  <option value="single">Single answer</option><option value="multi">Multi-select</option><option value="truefalse">True / False</option>
                </select>
                <select className="input" value={q.difficulty} onChange={(e) => updQ(qi, { difficulty: e.target.value })}>
                  {["Easy", "Medium", "Hard"].map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                {q.answers.map((a, ai) => (
                  <div key={ai} className="flex items-center gap-2">
                    <button onClick={() => setCorrect(qi, ai)} className={`h-6 w-6 rounded border flex items-center justify-center shrink-0 ${a.is_correct ? "bg-emerald-500 border-emerald-500" : "border-white/20"}`} title="Mark correct" data-testid={`qb-correct-${qi}-${ai}`}>
                      {a.is_correct && "✓"}
                    </button>
                    <input className="input" placeholder={`Option ${ai + 1}`} value={a.text} onChange={(e) => updA(qi, ai, { text: e.target.value })} />
                    {q.answers.length > 2 && <button className="btn-ghost !px-2 text-red-400" onClick={() => updQ(qi, { answers: q.answers.filter((_, i) => i !== ai) })}><Trash size={14} /></button>}
                  </div>
                ))}
                <button className="btn-ghost text-sm" onClick={() => updQ(qi, { answers: [...q.answers, { text: "", is_correct: false }] })}><Plus size={14} /> Add option</button>
              </div>
              <input className="input text-sm" placeholder="Explanation (shown after submit)" value={q.explanation} onChange={(e) => updQ(qi, { explanation: e.target.value })} />
            </div>
          ))}

          <button className="btn-secondary w-full" onClick={() => upd({ questions: [...quiz.questions, emptyQuestion()] })} data-testid="qb-add-question"><Plus size={16} /> Add question</button>
          <button className="btn-primary w-full" onClick={save} data-testid="qb-save"><Zap size={16} /> Save quiz</button>
        </div>
      )}
    </Modal>
  );
}
